// SettingsPanel.tsx
import React, { useState, useEffect } from 'react';
import { getSupabase, initSupabase } from '../../lib/api/supabase';
import { ExportSettings, Project, User } from '../../types';
import { Settings, Star, Eye, Layout, Palette, Type, ChevronDown, AlertCircle } from 'lucide-react';

const defaultSettings = {
  masterFont: 'Arial',
  masterFontSize: 10,
  masterFontColor: '#000000',
  headerBgColor: '#1E3A8A',
  headerFontColor: '#FFFFFF',
  subtotalBorder: '#000000',
  subtotalBgColor: '',
  subtotalFontColor: '#000000',
  subtotalStyle: '',
  sectionHeaderBgColor: '',
  sectionFontColor: '#000000',
  sectionSubtotalBgColor: '#E5E7EB', // light gray
  sectionTotalRowStyle: 'bold',
  mainTotalsFontColor: '#000000',
  mainTotalsBgColor: '#D3D3D3', // light gray
  mainTotalsStyle: 'bold',
};

// Scoped styles for this component only
const scopedStyles = `
  .settings-panel-scope {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #111827;
  }
  
  .settings-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    margin-bottom: 1.5rem;
  }
  
  .settings-card-header {
    padding: 1.5rem 1.5rem 0.75rem 1.5rem;
    border-bottom: 1px solid #f3f4f6;
  }
  
  .settings-card-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .settings-card-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }
  
  .settings-card-content {
    padding: 1.5rem;
  }
  
  .settings-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  
  .settings-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  .settings-button {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .settings-button-primary {
    background: #3b82f6;
    color: white;
  }
  
  .settings-button-primary:hover {
    background: #2563eb;
  }
  
  .settings-button-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
  
  .settings-button-outline {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
  }
  
  .settings-button-outline:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  
  .settings-button-sm {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }
  
  .settings-button-red {
    color: #dc2626;
    border-color: #fecaca;
  }
  
  .settings-button-red:hover {
    background: #fef2f2;
    border-color: #fca5a5;
  }
  
  .settings-border-left-blue {
    border-left: 4px solid #3b82f6;
  }
  
  .settings-template-item {
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f9fafb;
  }
  
  .settings-template-actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .settings-badge {
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .settings-badge-purple {
    background: #ede9fe;
    color: #7c3aed;
  }
  
  .settings-badge-green {
    background: #dcfce7;
    color: #16a34a;
  }
  
  .settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  .settings-form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .settings-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }
  
  .settings-color-input {
    width: 60px;
    height: 40px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    padding: 0;
  }
  
  .settings-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    cursor: pointer;
  }
  
  .settings-number-input {
    width: 80px;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
  }
  
  .settings-collapsible {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-bottom: 1rem;
  }
  
  .settings-collapsible-header {
    padding: 1rem;
    background: #f9fafb;
    border-radius: 6px 6px 0 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .settings-collapsible-content {
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }
  
  .settings-sidebar {
    width: 200px;
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    padding: 1rem 0;
  }
  
  .settings-sidebar-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }
  
  .settings-sidebar-item:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  .settings-sidebar-item.active {
    background: #dbeafe;
    color: #2563eb;
    border-right: 2px solid #3b82f6;
  }
  
  .settings-main-content {
    flex: 1;
    padding: 0 1.5rem;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function isValidHex(color: string) {
  return /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(color);
}

interface SettingsPanelProps {
  user: User;
  selectedProject: Project | null;
  onSetProjectExportSettings: (settingsId: string) => void;
  onProjectUpdated?: (updatedProject: Project) => void;
}

export default function SettingsPanel({ user, selectedProject, onSetProjectExportSettings, onProjectUpdated }: SettingsPanelProps) {
  const [settings, setSettings] = useState(defaultSettings);
  const [errors, setErrors] = useState<{[k: string]: string}>({});
  const [savedSettings, setSavedSettings] = useState<ExportSettings[]>([]);
  const [settingsName, setSettingsName] = useState('');
  const [activeSettingsId, setActiveSettingsId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [userId, setUserId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Project Settings state
  const [projectIndustry, setProjectIndustry] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);

  // Sidebar navigation state
  const [activeSection, setActiveSection] = useState<'project' | 'excel'>('project');

  // Clear status message when switching between sections
  const handleSectionChange = (section: 'project' | 'excel') => {
    setActiveSection(section);
    setStatusMsg(''); // Clear any existing status messages
  };

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    typography: false,
    header: false,
    sections: false,
    subtotals: false,
    mainTotals: false,
  });

  // Load project settings when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      setProjectIndustry(selectedProject.industry || '');
      setProjectDescription(selectedProject.business_description || '');
    }
  }, [selectedProject]);

  // Listen for settings tab clicks to auto-refresh
  useEffect(() => {
    const handleSettingsTabClick = () => {
      console.log('Settings tab clicked - auto-refreshing...');
      if (userId && isInitialized) {
        refreshSettingsData();
      }
    };

    window.addEventListener('settingsTabClicked', handleSettingsTabClick);
    return () => window.removeEventListener('settingsTabClicked', handleSettingsTabClick);
  }, [userId, isInitialized]);

  // Detect when the settings panel becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          console.log('Settings panel became visible - auto-refreshing...');
          if (userId) {
            refreshSettingsData();
          }
        }
      },
      { threshold: 0.1 }
    );

    const settingsElement = document.querySelector('[data-settings-panel]');
    if (settingsElement) {
      observer.observe(settingsElement);
    }

    return () => observer.disconnect();
  }, [userId, isVisible]);

  // Force refresh when component mounts or becomes visible
  const refreshSettingsData = async () => {
    if (!userId) return;
    
    console.log('Refreshing settings data...');
    await Promise.all([
      fetchSavedSettings(userId),
      loadActiveSettingsForProject()
    ]);
  };

  // Load the active settings for the current project
  const loadActiveSettingsForProject = async () => {
    if (!userId || !isInitialized) return;
    
    try {
      const supabase = getSupabase();
      let activeSettingsData = null;
      let activeId = null;

      // If we have a selected project, get its active settings
      if (selectedProject?.export_settings) {
        console.log('Loading project-specific settings:', selectedProject.export_settings);
        const { data, error } = await supabase
          .from('export_settings')
          .select('*')
          .eq('id', selectedProject.export_settings)
          .single();
        
        if (!error && data) {
          activeSettingsData = data;
          activeId = data.id;
        }
      }
      
      // If no project-specific settings, fall back to user's default
      if (!activeSettingsData) {
        console.log('Loading user default settings...');
        const { data, error } = await supabase
          .from('export_settings')
          .select('*')
          .eq('user_id', userId)
          .eq('is_default', true)
          .maybeSingle();
        
        if (!error && data) {
          activeSettingsData = data;
          activeId = data.id;
        }
      }

      // Apply the active settings to the form
      if (activeSettingsData) {
        console.log('Loading active settings:', activeSettingsData);
        setSettings(activeSettingsData.settings);
        setSettingsName(activeSettingsData.name);
        setActiveSettingsId(activeId);
        setStatusMsg('');
      } else {
        console.log('No active settings found, using defaults');
        setSettings(defaultSettings);
        setSettingsName('');
        setActiveSettingsId(null);
        setStatusMsg('');
      }
    } catch (error) {
      console.error('Error loading active settings:', error);
      setStatusMsg('Failed to load active settings.');
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Ensure Supabase is initialized first
        await initSupabase();
        const supabase = getSupabase();
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('No authenticated user');
        }
        
        console.log('SettingsPanel initialized for user:', user.id);
        setUserId(user.id);
        setIsInitialized(true);
        
        // Auto-load data on initialization
        setTimeout(() => {
          refreshSettingsData();
        }, 100);
        
      } catch (error) {
        console.error('Error initializing SettingsPanel:', error);
        setStatusMsg('Failed to initialize settings panel. Please sign in again.');
      }
    };
    init();
  }, []);

  // Refresh when selected project changes
  useEffect(() => {
    if (isInitialized && userId) {
      console.log('Selected project changed, refreshing settings...');
      loadActiveSettingsForProject();
    }
  }, [selectedProject?.id, isInitialized, userId]);

  const fetchSavedSettings = async (userId: string) => {
    if (!isInitialized) return;
    
    setIsLoading(true);
    try {
      const supabase = getSupabase();
      console.log('Fetching settings for user:', userId);
      
      const { data, error } = await supabase
        .from('export_settings')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      console.log('Fetch results:', { data, error });
      
      if (error) {
        console.error('Error fetching settings:', error);
        if (error.code === 'PGRST116') {
          setStatusMsg('Export settings table not found. Please contact support.');
        } else if (error.code?.startsWith('PGRST')) {
          setStatusMsg('Database schema issue. Please refresh the page and try again.');
        } else {
          setStatusMsg('Failed to load saved settings.');
        }
        return;
      }
      
      if (data) {
        console.log('Setting saved settings:', data);
        setSavedSettings(data);
        if (data.length === 0) {
          setStatusMsg('No saved export templates found.');
        } else {
          // Don't show a status message for successful loading - it's just clutter
          setStatusMsg('');
        }
      }
    } catch (error) {
      console.error('Error fetching saved settings:', error);
      setStatusMsg('Failed to load saved settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProjectSettings = async () => {
    if (!selectedProject) return;
    
    setIsUpdatingProject(true);
    try {
      // Use the existing Supabase client from your app
      const supabase = getSupabase();
      
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      
      if (projectIndustry.trim()) updateData.industry = projectIndustry.trim();
      if (projectDescription.trim()) updateData.business_description = projectDescription.trim();
      
      console.log('Updating project directly via Supabase:', updateData);
      
      const { data, error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', selectedProject.id)
        .select();  // Remove .single() to avoid the multiple rows error
      
      if (error) {
        console.error('Supabase update error:', error);
        setStatusMsg(`Failed to update project settings: ${error.message}`);
        return;
      }
      
      if (!data || data.length === 0) {
        setStatusMsg('No project was updated. Please check the project ID.');
        return;
      }
      
      console.log('Project updated successfully:', data[0]);
      setStatusMsg('Project settings updated successfully!');
      
      // Notify parent component of the project update
      if (onProjectUpdated) {
        onProjectUpdated(data[0]);
      }
      
    } catch (error) {
      console.error('Error updating project settings:', error);
      setStatusMsg('Failed to update project settings.');
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const handleSave = async () => {
    if (!settingsName.trim()) {
      setStatusMsg('Please enter a name for your settings.');
      return;
    }
    
    if (!isInitialized || !userId) {
      setStatusMsg('Settings panel not properly initialized. Please refresh the page.');
      return;
    }
    
    setIsLoading(true);
    try {
      const supabase = getSupabase();
      
      // Check if settings with this name already exist
      const { data: existingSettings, error: checkError } = await supabase
        .from('export_settings')
        .select('id')
        .eq('user_id', userId)
        .eq('name', settingsName.trim())
        .maybeSingle();
      
      if (checkError) {
        throw checkError;
      }
      
      let result;
      
      if (existingSettings) {
        // Update existing settings
        result = await supabase
          .from('export_settings')
          .update({
            settings,
            is_default: savedSettings.length === 0, // first one is default
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id)
          .select()
          .single();
      } else {
        // Insert new settings
        result = await supabase
          .from('export_settings')
          .insert({
            user_id: userId,
            name: settingsName.trim(),
            settings,
            is_default: savedSettings.length === 0, // first one is default
          })
          .select()
          .single();
      }
      
      const { data, error } = result;
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setStatusMsg('Settings saved successfully!');
        setSavedSettings(prev => {
          const idx = prev.findIndex(s => s.id === data.id);
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = data;
            return updated;
          }
          return [data, ...prev];
        });
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      
      // Handle specific error types
      if (error.code === 'PGRST204') {
        setStatusMsg('Database schema error. Please refresh the page and try again.');
      } else if (error.code === '23505') {
        setStatusMsg('A settings configuration with this name already exists.');
      } else {
        setStatusMsg('Failed to save settings.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoad = (settingsToLoad: ExportSettings) => {
    setSettings(settingsToLoad.settings);
    setSettingsName(settingsToLoad.name);
    setStatusMsg(`Loaded settings: ${settingsToLoad.name}`);
  };

  const handleSetActive = async (settingsId: string) => {
    if (!selectedProject) return;
    
    setIsLoading(true);
    try {
      const supabase = getSupabase();
      
      const { error } = await supabase
        .from('projects')
        .update({ export_settings: settingsId })
        .eq('id', selectedProject.id);
      
      if (error) {
        throw error;
      }
      
      setActiveSettingsId(settingsId);
      onSetProjectExportSettings(settingsId);
      setStatusMsg('Active settings updated for this project!');
    } catch (error) {
      console.error('Error setting active settings:', error);
      setStatusMsg('Failed to set active settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (settingsId: string) => {
    setIsLoading(true);
    try {
      const supabase = getSupabase();
      
      // First, remove default from all settings
      await supabase
        .from('export_settings')
        .update({ is_default: false })
        .eq('user_id', userId);
      
      // Then set the new default
      const { error } = await supabase
        .from('export_settings')
        .update({ is_default: true })
        .eq('id', settingsId);
      
      if (error) {
        throw error;
      }
      
      // Refresh the settings list
      await fetchSavedSettings(userId);
      setStatusMsg('Default settings updated!');
    } catch (error) {
      console.error('Error setting default settings:', error);
      setStatusMsg('Failed to set default settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (settingsId: string) => {
    if (!confirm('Are you sure you want to delete this settings configuration?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const supabase = getSupabase();
      
      const { error } = await supabase
        .from('export_settings')
        .delete()
        .eq('id', settingsId);
      
      if (error) {
        throw error;
      }
      
      // Remove from local state
      setSavedSettings(prev => prev.filter(s => s.id !== settingsId));
      
      // If this was the active settings, clear it
      if (activeSettingsId === settingsId) {
        setActiveSettingsId(null);
        setSettings(defaultSettings);
        setSettingsName('');
      }
      
      setStatusMsg('Settings deleted successfully!');
    } catch (error) {
      console.error('Error deleting settings:', error);
      setStatusMsg('Failed to delete settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Clear any existing error for this field
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Loading state
  if (!isInitialized) {
    return (
      <div className="settings-panel-scope" style={{ maxWidth: "1200px", margin: "0 auto", padding: "1.5rem" }}>
        <style>{scopedStyles}</style>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <Settings style={{ width: "1.5rem", height: "1.5rem", color: "#3b82f6" }} />
          <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#111827", margin: 0 }}>Settings</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
          <div style={{ width: "2rem", height: "2rem", border: "2px solid #3b82f6", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          <span style={{ marginLeft: 12, color: "#6b7280" }}>Loading settings...</span>
        </div>
      </div>
    );
  }

  const activeSettings = savedSettings.find((s) => s.id === activeSettingsId);

  return (
    <div className="settings-panel-scope" style={{ maxWidth: "1200px", margin: "0 auto", padding: "1.5rem" }} data-settings-panel>
      <style>{scopedStyles}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <Settings style={{ width: "1.5rem", height: "1.5rem", color: "#3b82f6" }} />
        <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#111827", margin: 0 }}>Settings</h1>
      </div>
      
      {/* Main Layout with Sidebar */}
      <div style={{ display: "flex", gap: 0, background: "white", borderRadius: "8px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          <button
            onClick={() => handleSectionChange('project')}
            className={`settings-sidebar-item ${activeSection === 'project' ? 'active' : ''}`}
          >
            <Settings style={{ width: "1.25rem", height: "1.25rem" }} />
            Project Settings
          </button>
          <button
            onClick={() => handleSectionChange('excel')}
            className={`settings-sidebar-item ${activeSection === 'excel' ? 'active' : ''}`}
          >
            <Layout style={{ width: "1.25rem", height: "1.25rem" }} />
            Excel Export Settings
          </button>
        </div>
        
        {/* Main Content Area */}
        <div className="settings-main-content">

          {/* Project Settings Section */}
          {activeSection === 'project' && (
            <div style={{ padding: "1.5rem 0" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827", margin: "0 0 0.5rem 0" }}>
                  Project Settings
                </h2>
                <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: 0 }}>
                  This context is used to enhance AI-generated QoE reports and AI chat with industry-specific insights and business context
                </p>
              </div>
              
              {selectedProject ? (
                <div style={{ display: "grid", gap: "1rem", maxWidth: "600px" }}>
                  <div>
                    <label style={{ 
                      display: "block", 
                      fontSize: "0.875rem", 
                      fontWeight: "500", 
                      color: "#374151", 
                      marginBottom: "0.5rem" 
                    }}>
                      Industry
                    </label>
                    <input
                      type="text"
                      value={projectIndustry}
                      onChange={(e) => setProjectIndustry(e.target.value)}
                      placeholder="e.g., Technology, Healthcare, Manufacturing"
                      className="settings-input"
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: "block", 
                      fontSize: "0.875rem", 
                      fontWeight: "500", 
                      color: "#374151", 
                      marginBottom: "0.5rem" 
                    }}>
                      Business Description
                    </label>
                    <textarea
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Brief description of the business (e.g., SaaS platform for small businesses, Medical device manufacturer)"
                      className="settings-input"
                      rows={3}
                      style={{ resize: "vertical" }}
                    />
                  </div>
                  
                  <div style={{ display: "flex", justifyContent: "flex-start", gap: "0.5rem" }}>
                    <button
                      onClick={handleUpdateProjectSettings}
                      disabled={isUpdatingProject}
                      className="settings-button settings-button-primary"
                    >
                      {isUpdatingProject ? 'Saving...' : 'Save Project Settings'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", color: "#6b7280", padding: "2rem" }}>
                  <AlertCircle style={{ width: "3rem", height: "3rem", margin: "0 auto 1rem", opacity: 0.5 }} />
                  <p style={{ fontSize: "0.875rem", margin: 0 }}>
                    No project selected. Please select a project from the Data Sources tab to configure project settings.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Excel Export Settings Section */}
          {activeSection === 'excel' && (
            <div style={{ padding: "1.5rem 0" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827", margin: "0 0 0.5rem 0" }}>
                  Excel Export Settings
                </h2>
                <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: 0 }}>
                  Customize the appearance and formatting of your Excel exports
                </p>
              </div>
              
              {/* Show message if no active settings */}
              {!activeSettings && (
                <div style={{ 
                  marginBottom: "1.5rem", 
                  padding: "1rem", 
                  background: "#fefce8", 
                  border: "1px solid #eab308", 
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem"
                }}>
                  <AlertCircle style={{ width: "1.25rem", height: "1.25rem", color: "#eab308", flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: "0.875rem", fontWeight: "500", color: "#92400e", marginBottom: "0.25rem" }}>
                      No active settings found
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#a16207" }}>
                      Create a new configuration below to customize your Excel exports
                    </div>
                  </div>
                </div>
              )}
              
              {/* Active Settings Display - Only show when there are active settings */}
              {activeSettings && (
                <div className="settings-card settings-border-left-blue" style={{ marginBottom: "1.5rem" }}>
                  <div className="settings-card-header">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div className="settings-card-title">
                        <Star style={{ width: "1.25rem", height: "1.25rem", color: "#eab308" }} />
                        Active Settings Template
                      </div>
                      <button
                        className="settings-button settings-button-outline settings-button-sm"
                        onClick={refreshSettingsData}
                        disabled={isLoading}
                      >
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="settings-card-content">
                    {isLoading ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", justifyContent: "center" }}>
                        <div style={{ width: "1.5rem", height: "1.5rem", border: "2px solid #3b82f6", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                        <span style={{ color: "#6b7280" }}>Loading...</span>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                          <span style={{ fontSize: "1rem", fontWeight: "500" }}>{activeSettings.name}</span>
                          <div style={{ display: "flex", gap: "0.25rem" }}>
                            {activeSettings.is_default && <span className="settings-badge settings-badge-purple">Default</span>}
                            <span className="settings-badge settings-badge-green">Active</span>
                          </div>
                        </div>
                        <p style={{ color: "#6b7280", fontSize: "0.875rem", margin: 0 }}>
                          This template will be used for Excel exports from this project.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Saved Templates */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-title">
                    <Eye style={{ width: "1.25rem", height: "1.25rem", color: "#7c3aed" }} />
                    Saved Templates
                  </div>
                  <p className="settings-card-description">Manage your saved export settings templates</p>
                </div>
                <div className="settings-card-content">
                  {savedSettings.length === 0 ? (
                    <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                      No saved templates yet. Create your first configuration below.
                    </p>
                  ) : (
                    <div style={{ display: "grid", gap: "0.75rem" }}>
                      {savedSettings.map((s) => (
                        <div key={s.id} className="settings-template-item">
                          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <button
                              onClick={() => handleLoad(s)}
                              className="settings-button"
                              style={{ fontWeight: 500, color: "#2563eb", background: "none", border: "none", padding: 0 }}
                            >
                              {s.name}
                            </button>
                            <div style={{ display: "flex", gap: "0.25rem" }}>
                              {s.is_default && <span className="settings-badge settings-badge-purple">Default</span>}
                              {activeSettingsId === s.id && <span className="settings-badge settings-badge-green">Active</span>}
                           </div>
                         </div>
                         <div className="settings-template-actions">
                           {selectedProject && (
                             <button
                               onClick={() => handleSetActive(s.id)}
                               className="settings-button settings-button-outline settings-button-sm"
                             >
                               Set Active
                             </button>
                           )}
                           <button
                             onClick={() => handleSetDefault(s.id)}
                             className="settings-button settings-button-outline settings-button-sm"
                           >
                             Set Default
                           </button>
                           <button
                             onClick={() => handleDelete(s.id)}
                             className="settings-button settings-button-outline settings-button-sm settings-button-red"
                           >
                             Delete
                           </button>
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>

              {/* Save New Template */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-title">Save Current Configuration</div>
                  <p className="settings-card-description">Save your current settings as a new template</p>
                </div>
                <div className="settings-card-content">
                  <div style={{ display: "flex", gap: "0.75rem" }}>
                    <input
                      type="text"
                      placeholder="Template name (e.g., 'Corporate Style', 'Clean Format')"
                      value={settingsName}
                      onChange={e => setSettingsName(e.target.value)}
                      className="settings-input"
                      style={{ flex: 1 }}
                    />
                    <button className="settings-button settings-button-primary" onClick={handleSave} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save Template'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Excel Export Configuration */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <div className="settings-card-title">
                    <Palette style={{ width: "1.25rem", height: "1.25rem", color: "#059669" }} />
                    Export Formatting Options
                  </div>
                  <p className="settings-card-description">Customize colors, fonts, and styling for your Excel exports</p>
                </div>
                <div className="settings-card-content">
         
         {/* Typography Section - Global Settings */}
         <div className="settings-collapsible">
           <div
             className="settings-collapsible-header"
             onClick={() => toggleSection('typography')}
           >
             <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
               <Type style={{ width: "1.25rem", height: "1.25rem", color: "#6b7280" }} />
               <span style={{ fontWeight: "500" }}>Typography (Global)</span>
             </div>
             <ChevronDown
               style={{
                 width: "1.25rem",
                 height: "1.25rem",
                 color: "#6b7280",
                 transform: expandedSections.typography ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.2s ease"
               }}
             />
           </div>
           {expandedSections.typography && (
             <div className="settings-collapsible-content">
               <div className="settings-grid">
                 <div className="settings-form-group">
                   <label className="settings-label">Master Font</label>
                   <select
                     className="settings-select"
                     value={settings.masterFont}
                     onChange={e => handleSettingChange('masterFont', e.target.value)}
                   >
                     <option value="Arial">Arial</option>
                     <option value="Helvetica">Helvetica</option>
                     <option value="Times New Roman">Times New Roman</option>
                     <option value="Calibri">Calibri</option>
                     <option value="Verdana">Verdana</option>
                   </select>
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Size</label>
                   <input
                     type="number"
                     className="settings-number-input"
                     value={settings.masterFontSize}
                     onChange={e => handleSettingChange('masterFontSize', parseInt(e.target.value))}
                     min="6"
                     max="20"
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.masterFontColor}
                     onChange={e => handleSettingChange('masterFontColor', e.target.value)}
                   />
                 </div>
               </div>
             </div>
           )}
         </div>

         {/* Header Row Section */}
         <div className="settings-collapsible">
           <div
             className="settings-collapsible-header"
             onClick={() => toggleSection('header')}
           >
             <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
               <Palette style={{ width: "1.25rem", height: "1.25rem", color: "#6b7280" }} />
               <span style={{ fontWeight: "500" }}>Header Row</span>
             </div>
             <ChevronDown
               style={{
                 width: "1.25rem",
                 height: "1.25rem",
                 color: "#6b7280",
                 transform: expandedSections.header ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.2s ease"
               }}
             />
           </div>
           {expandedSections.header && (
             <div className="settings-collapsible-content">
               <div className="settings-grid">
                 <div className="settings-form-group">
                   <label className="settings-label">Background Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.headerBgColor}
                     onChange={e => handleSettingChange('headerBgColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.headerFontColor}
                     onChange={e => handleSettingChange('headerFontColor', e.target.value)}
                   />
                 </div>
               </div>
             </div>
           )}
         </div>

         {/* Section Headers */}
         <div className="settings-collapsible">
           <div
             className="settings-collapsible-header"
             onClick={() => toggleSection('sections')}
           >
             <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
               <Layout style={{ width: "1.25rem", height: "1.25rem", color: "#6b7280" }} />
               <span style={{ fontWeight: "500" }}>Section Headers</span>
             </div>
             <ChevronDown
               style={{
                 width: "1.25rem",
                 height: "1.25rem",
                 color: "#6b7280",
                 transform: expandedSections.sections ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.2s ease"
               }}
             />
           </div>
           {expandedSections.sections && (
             <div className="settings-collapsible-content">
               <div className="settings-grid">
                 <div className="settings-form-group">
                   <label className="settings-label">Background Color</label>
                   <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                     <input
                       type="color"
                       className="settings-color-input"
                       value={settings.sectionHeaderBgColor || '#ffffff'}
                       onChange={e => handleSettingChange('sectionHeaderBgColor', e.target.value)}
                       disabled={!settings.sectionHeaderBgColor}
                     />
                     <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", cursor: "pointer" }}>
                       <input
                         type="checkbox"
                         checked={!!settings.sectionHeaderBgColor}
                         onChange={e => handleSettingChange('sectionHeaderBgColor', e.target.checked ? '#f3f4f6' : '')}
                       />
                       Enable
                     </label>
                   </div>
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.sectionFontColor}
                     onChange={e => handleSettingChange('sectionFontColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Subtotal Background Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.sectionSubtotalBgColor || '#E5E7EB'}
                     onChange={e => handleSettingChange('sectionSubtotalBgColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Total Row Style</label>
                   <select
                     className="settings-select"
                     value={settings.sectionTotalRowStyle}
                     onChange={e => handleSettingChange('sectionTotalRowStyle', e.target.value)}
                   >
                     <option value="">Normal</option>
                     <option value="bold">Bold</option>
                     <option value="italic">Italic</option>
                     <option value="underline">Underline</option>
                   </select>
                 </div>
               </div>
             </div>
           )}
         </div>

         {/* Subtotals Section */}
         <div className="settings-collapsible">
           <div
             className="settings-collapsible-header"
             onClick={() => toggleSection('subtotals')}
           >
             <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
               <Layout style={{ width: "1.25rem", height: "1.25rem", color: "#6b7280" }} />
               <span style={{ fontWeight: "500" }}>Subtotals</span>
             </div>
             <ChevronDown
               style={{
                 width: "1.25rem",
                 height: "1.25rem",
                 color: "#6b7280",
                 transform: expandedSections.subtotals ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.2s ease"
               }}
             />
           </div>
           {expandedSections.subtotals && (
             <div className="settings-collapsible-content">
               <div className="settings-grid">
                 <div className="settings-form-group">
                   <label className="settings-label">Background Color</label>
                   <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                     <input
                       type="color"
                       className="settings-color-input"
                       value={settings.subtotalBgColor || '#ffffff'}
                       onChange={e => handleSettingChange('subtotalBgColor', e.target.value)}
                       disabled={!settings.subtotalBgColor}
                     />
                     <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", cursor: "pointer" }}>
                       <input
                         type="checkbox"
                         checked={!!settings.subtotalBgColor}
                         onChange={e => handleSettingChange('subtotalBgColor', e.target.checked ? '#f3f4f6' : '')}
                       />
                       Enable
                     </label>
                   </div>
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.subtotalFontColor}
                     onChange={e => handleSettingChange('subtotalFontColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Border Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.subtotalBorder}
                     onChange={e => handleSettingChange('subtotalBorder', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Style</label>
                   <select
                     className="settings-select"
                     value={settings.subtotalStyle}
                     onChange={e => handleSettingChange('subtotalStyle', e.target.value)}
                   >
                     <option value="">Normal</option>
                     <option value="bold">Bold</option>
                     <option value="italic">Italic</option>
                     <option value="underline">Underline</option>
                   </select>
                 </div>
               </div>
             </div>
           )}
         </div>

         {/* Main Totals Section */}
         <div className="settings-collapsible">
           <div
             className="settings-collapsible-header"
             onClick={() => toggleSection('mainTotals')}
           >
             <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
               <Layout style={{ width: "1.25rem", height: "1.25rem", color: "#6b7280" }} />
               <span style={{ fontWeight: "500" }}>Main Totals</span>
             </div>
             <ChevronDown
               style={{
                 width: "1.25rem",
                 height: "1.25rem",
                 color: "#6b7280",
                 transform: expandedSections.mainTotals ? "rotate(180deg)" : "rotate(0deg)",
                 transition: "transform 0.2s ease"
               }}
             />
           </div>
           {expandedSections.mainTotals && (
             <div className="settings-collapsible-content">
               <div className="settings-grid">
                 <div className="settings-form-group">
                   <label className="settings-label">Background Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.mainTotalsBgColor}
                     onChange={e => handleSettingChange('mainTotalsBgColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Font Color</label>
                   <input
                     type="color"
                     className="settings-color-input"
                     value={settings.mainTotalsFontColor}
                     onChange={e => handleSettingChange('mainTotalsFontColor', e.target.value)}
                   />
                 </div>
                 <div className="settings-form-group">
                   <label className="settings-label">Style</label>
                   <select
                     className="settings-select"
                     value={settings.mainTotalsStyle}
                     onChange={e => handleSettingChange('mainTotalsStyle', e.target.value)}
                   >
                     <option value="">Normal</option>
                     <option value="bold">Bold</option>
                     <option value="italic">Italic</option>
                     <option value="underline">Underline</option>
                   </select>
                 </div>
               </div>
             </div>
           )}
         </div>

                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Status Message */}
      {statusMsg && (
        <div style={{ 
          marginTop: "1rem", 
          padding: "0.75rem", 
          background: "#f0f9ff", 
          border: "1px solid #0ea5e9", 
          borderRadius: "6px", 
          fontSize: "0.875rem",
          color: "#0c4a6e"
        }}>
          {statusMsg}
        </div>
      )}
    </div>
 );
}